import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const HierarchicalItem = React.memo(
  ({
    item,
    renderItem,
    depth = 0,
    isCollapsed,
    onToggleCollapse,
    collapseIcon,
    isParent,
    isItemCollapsed,
    activeId,
    getAllChildren,
    isChildOfDraggedItem,
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.id });

    // Check if this item is a child of the currently dragged item
    const isChildOfDragged = useMemo(() => {
      return isChildOfDraggedItem ? isChildOfDraggedItem(item.id, activeId) : false;
    }, [item.id, activeId, isChildOfDraggedItem]);

    const style = useMemo(() => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : (isChildOfDragged ? 0.3 : 1),
    }), [transform, transition, isDragging, isChildOfDragged]);

    const handleToggleCollapse = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggleCollapse(item.id);
      },
      [item.id, onToggleCollapse]
    );

    // Only show collapse icon for parent items with children
    const shouldShowCollapseIcon =
      isParent && item.children && item.children.length > 0;

    // Memoize the rendered item to prevent unnecessary re-renders
    const renderedItem = useMemo(() => renderItem({
      collapseIcon: shouldShowCollapseIcon ? (
        <div
          onClick={handleToggleCollapse}
          style={{ cursor: "pointer", display: "inline-block" }}
        >
          {collapseIcon({ isCollapsed, item })}
        </div>
      ) : null,
      depth,
      handler: null,
      index: 0,
      item,
    }), [renderItem, shouldShowCollapseIcon, handleToggleCollapse, collapseIcon, isCollapsed, item, depth]);

    // Memoize children rendering
    const childrenContent = useMemo(() => {
      if (isCollapsed || !item.children || item.children.length === 0) {
        return null;
      }

      return (
        <div
          style={{
            backgroundColor: depth > 0 ? "#F9F8F7" : "transparent",
            marginTop: depth > 0 ? "10px" : "0",
            paddingLeft: "20px",
          }}
        >
          <div style={{ marginLeft: depth * 20 }}>
            {item.children.map((child) => (
              <HierarchicalItem
                key={child.id}
                item={child}
                renderItem={renderItem}
                depth={depth + 1}
                isCollapsed={isItemCollapsed(child.id)}
                onToggleCollapse={onToggleCollapse}
                collapseIcon={collapseIcon}
                isParent={child.children && child.children.length > 0}
                isItemCollapsed={isItemCollapsed}
                activeId={activeId}
                getAllChildren={getAllChildren}
                isChildOfDraggedItem={isChildOfDraggedItem}
              />
            ))}
          </div>
        </div>
      );
    }, [isCollapsed, item.children, depth, renderItem, isItemCollapsed, onToggleCollapse, collapseIcon, activeId, getAllChildren, isChildOfDraggedItem]);

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {renderedItem}
        {childrenContent}
      </div>
    );
  }
);

HierarchicalItem.displayName = "HierarchicalItem";

const NestableSortable = React.memo(
  ({ items, renderItem, renderCollapseIcon, className, maxDepth = 10, onMoveItem }) => {
    const [activeId, setActiveId] = useState(null);
    const [collapsedItems, setCollapsedItems] = useState(new Set());
    const [activeItem, setActiveItem] = useState(null);
    
    // Use refs to prevent unnecessary re-renders
    const itemsRef = useRef(items);
    const onMoveItemRef = useRef(onMoveItem);
    
    // Update refs when props change
    React.useEffect(() => {
      itemsRef.current = items;
      onMoveItemRef.current = onMoveItem;
    }, [items, onMoveItem]);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8, // Only start dragging after 8px movement
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    // Memoize flattened items with better performance
    const flattenedItems = useMemo(() => {
      const flatten = (items, level = 0) => {
        const result = [];
        const stack = [{ items, level }];
        
        while (stack.length > 0) {
          const { items: currentItems, level: currentLevel } = stack.pop();
          
          for (let i = currentItems.length - 1; i >= 0; i--) {
            const item = currentItems[i];
            result.unshift({ ...item, level: currentLevel });
            
            if (item.children && item.children.length > 0 && !collapsedItems.has(item.id)) {
              stack.push({ items: item.children, level: currentLevel + 1 });
            }
          }
        }
        
        return result;
      };
      return flatten(items);
    }, [items, collapsedItems]);

    // Memoize item IDs for SortableContext
    const itemIds = useMemo(
      () => flattenedItems.map((item) => item.id),
      [flattenedItems]
    );

    // Optimized toggle collapse function
    const toggleCollapse = useCallback((itemId) => {
      setCollapsedItems((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(itemId)) {
          newSet.delete(itemId);
        } else {
          newSet.add(itemId);
        }
        return newSet;
      });
    }, []);

    // Check if an item is collapsed
    const isItemCollapsed = useCallback(
      (itemId) => collapsedItems.has(itemId),
      [collapsedItems]
    );

    // Helper function to get all children of an item recursively
    const getAllChildren = useCallback((item, allItems = []) => {
      if (item.children && item.children.length > 0) {
        item.children.forEach(child => {
          allItems.push(child);
          getAllChildren(child, allItems);
        });
      }
      return allItems;
    }, []);

    // Helper function to check if an item is a child of the dragged item
    const isChildOfDraggedItem = useCallback((itemId, draggedItemId) => {
      if (!draggedItemId) return false;
      
      const findItemInTree = (items, targetId) => {
        for (const currentItem of items) {
          if (currentItem.id === targetId) {
            return currentItem;
          }
          if (currentItem.children && currentItem.children.length > 0) {
            const found = findItemInTree(currentItem.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };
      
      const draggedItem = findItemInTree(items, draggedItemId);
      if (!draggedItem) return false;
      
      const allChildren = getAllChildren(draggedItem);
      return allChildren.some(child => child.id === itemId);
    }, [items, getAllChildren]);

    // Helper function to check if target is a child of dragged item (for validation)
    const isTargetChildOfDragged = useCallback((draggedItem, targetItem) => {
      if (!draggedItem || !targetItem) return false;
      
      const allChildren = getAllChildren(draggedItem);
      return allChildren.some(child => child.id === targetItem.id);
    }, [getAllChildren]);



    // Optimized drag start handler
    const handleDragStart = useCallback((event) => {
      const { active } = event;
      setActiveId(active.id);
      const item = flattenedItems.find((item) => item.id === active.id);
      setActiveItem(item);
    }, [flattenedItems]);

    // Optimized drag end handler with debouncing
    const handleDragEnd = useCallback((event) => {
      const { active, over } = event;
      setActiveId(null);
      setActiveItem(null);

      if (active.id !== over?.id && over) {
        const oldIndex = flattenedItems.findIndex(
          (item) => item.id === active.id
        );
        const newIndex = flattenedItems.findIndex(
          (item) => item.id === over.id
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          // Use requestAnimationFrame to prevent blocking the UI
          requestAnimationFrame(() => {
            try {
              // Get the dragged and target items
              const draggedItem = flattenedItems[oldIndex];
              const targetItem = flattenedItems[newIndex];
              
              // Determine if this is a horizontal drop (child relationship) or vertical drop (sibling)
              const isHorizontalDrop = Math.abs(event.delta.x) > 20 && event.delta.x > 0;
              
              

              // Validate that we're not trying to drag an item into its own child
              if (isTargetChildOfDragged(draggedItem, targetItem)) {
                return; // Exit early without making changes
              }
              
              // Create new array with reordered items
              const newFlattenedItems = [...flattenedItems];
              const [movedItem] = newFlattenedItems.splice(oldIndex, 1);
              
              if (isHorizontalDrop) {
                // Horizontal drop - make dragged item a child of target
                movedItem.level = targetItem.level + 1;
                // Find the target item in the new array and add as child
                const targetIndex = newFlattenedItems.findIndex(item => item.id === targetItem.id);
                if (targetIndex !== -1) {
                  newFlattenedItems.splice(targetIndex + 1, 0, movedItem);
                }
                
              } else {
                // Vertical drop - reorder at same level
                // Ensure the moved item stays at the same level as the target
                movedItem.level = targetItem.level;
                newFlattenedItems.splice(newIndex, 0, movedItem);
              }



              // Call onMoveItem callback for API updates
              if (onMoveItemRef.current) {
                onMoveItemRef.current(active.id, over.id, isHorizontalDrop);
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error during drag and drop:', error);
            }
          });
        }
      }
    }, [flattenedItems]);

    // Memoize the main content to prevent unnecessary re-renders
    const mainContent = useMemo(() => (
      <SortableContext
        items={itemIds}
        strategy={verticalListSortingStrategy}
      >
        {items.map((item) => (
          <HierarchicalItem
            key={item.id}
            item={item}
            renderItem={renderItem}
            depth={item.children && item.children.length > 0 ? 1 : 0}
            isCollapsed={isItemCollapsed(item.id)}
            onToggleCollapse={toggleCollapse}
            collapseIcon={renderCollapseIcon}
            isParent={item.children && item.children.length > 0}
            isItemCollapsed={isItemCollapsed}
            activeId={activeId}
            getAllChildren={getAllChildren}
            isChildOfDraggedItem={isChildOfDraggedItem}
          />
        ))}
      </SortableContext>
    ), [itemIds, items, renderItem, isItemCollapsed, toggleCollapse, renderCollapseIcon, activeId, getAllChildren, isChildOfDraggedItem]);

    // Memoize drag overlay with parent-child grouping
    const dragOverlay = useMemo(() => (
      <DragOverlay>
        {activeId && activeItem ? (
          <div style={{ opacity: 0.8 }}>
            {/* Render the main dragged item */}
            <div style={{ 
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}>
              {renderItem({
                collapseIcon: null,
                depth: 0,
                handler: null,
                index: 0,
                item: activeItem,
              })}
            </div>
            
            {/* Render all children if the dragged item has children */}
            {activeItem.children && activeItem.children.length > 0 && (
              <div style={{
                marginTop: '4px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}>
                {getAllChildren(activeItem).map((child, index) => (
                  <div key={child.id} style={{
                    borderBottom: index < getAllChildren(activeItem).length - 1 ? '1px solid #f2f3f5' : 'none',
                    paddingLeft: '20px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    {renderItem({
                      collapseIcon: null,
                      depth: 1,
                      handler: null,
                      index: index,
                      item: child,
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </DragOverlay>
    ), [activeId, activeItem, renderItem, getAllChildren]);

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className={className}>
          {mainContent}
        </div>
        {dragOverlay}
      </DndContext>
    );
  }
);

NestableSortable.displayName = "NestableSortable";

export default NestableSortable;
